/**
 * @file FreeGrantsSection.tsx
 * @brief Super Admin → Billing → Plans: "Free minutes once per device"
 *        (BACKEND B43, 31-10-2026, ADR-061), web + Mac app.
 *
 * @details
 * Contract: /api/admin/free-grants. Shows whether the check can run (salt
 * secret, Apple DeviceCheck key), how many devices and decisions exist, and
 * the support tools: find an account by email, give it its free minutes
 * ("Allow free minutes"), clear an Android / Mac device for the next account
 * (an iPhone keeps Apple's bit, so allow the account instead), and the
 * disposable email list (block a domain, allow one the list blocks, refresh
 * the list now). The on/off switch is "Free minutes once per device" on the
 * Free card. Device hashes are shown as their first 8 characters only.
 *
 * @author DES (prepared with Claude)
 * @date 31-10-2026
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { GridBand, GridCell } from "@/features/analytics/components/GridBlock";
import { ApiClientError, apiDelete, apiGet, apiPost, apiPut } from "@/shared/api/client";
import { adminEndpoints } from "@/shared/api/endpoints";
import { toast } from "@/shared/hooks/useToast";

const num = z.number().catch(0);
const SummarySchema = z.object({
  summary: z.object({
    deviceCheckEnabled: z.boolean().catch(true),
    saltSet: z.boolean().catch(false),
    appleDeviceCheckSet: z.boolean().catch(false),
    devices: z.array(z.object({ platform: z.string(), n: num })).catch([]),
    decisions: z.array(z.object({ outcome: z.string(), n: num })).catch([]),
  }),
  accounts: z
    .array(
      z.object({
        id: z.string(),
        email: z.string().nullable(),
        outcome: z.string().nullable(),
        platform: z.string().nullable(),
        decided_at: z.string().nullable(),
        override_at: z.string().nullable(),
        devices: z
          .array(
            z.object({
              id: z.union([z.number(), z.string()]),
              platform: z.string(),
              hash_start: z.string().nullable(),
              granted_at: z.string().nullable(),
              cleared_at: z.string().nullable(),
            }),
          )
          .catch([]),
      }),
    )
    .catch([]),
});
const DisposableSchema = z.object({
  listSize: num,
  refreshedAt: z.string().nullable().catch(null),
  adminRows: z.array(z.object({ domain: z.string(), blocked: z.boolean() })).catch([]),
  match: z.array(z.object({ domain: z.string(), source: z.string(), blocked: z.boolean() })).catch([]),
});

/** "Granted" / "Device already used" / "Email not allowed", for a stored outcome. */
function outcomeLabel(outcome: string | null | undefined): string {
  if (outcome === "granted") return "Free minutes given";
  if (outcome === "device_used") return "Refused: device already used";
  if (outcome === "email_not_allowed") return "Refused: disposable email";
  return "Not decided yet (no free recording)";
}

const errText = (e: unknown) => (e instanceof ApiClientError ? e.message : "Please try again.");
const KEY = ["admin", "free-grants"] as const;

export function FreeGrantsSection() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [domain, setDomain] = useState("");
  const grants = useQuery({
    queryKey: [...KEY, submitted],
    queryFn: async () =>
      SummarySchema.parse(await apiGet<unknown>(adminEndpoints.freeGrants, { params: submitted ? { search: submitted } : {} })),
  });
  const disposable = useQuery({
    queryKey: [...KEY, "disposable"],
    queryFn: async () => DisposableSchema.parse(await apiGet<unknown>(`${adminEndpoints.freeGrants}/disposable`)),
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: KEY });
  const act = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => {
      toast({ title: "Saved" });
      refresh();
    },
    onError: (e) => toast({ variant: "destructive", title: "Could not save", description: errText(e) }),
  });

  const s = grants.data?.summary;
  const count = (rows: { n: number }[] | undefined) => (rows ?? []).reduce((a, r) => a + r.n, 0);

  return (
    <>
      <GridBand className="py-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Free</p>
        <h3 className="mt-0.5 text-base font-semibold tracking-tight">Free minutes once per device</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Free minutes are given once per account and once per iPhone, Android phone or Mac. A second account on a
          used device opens normally with 0 free minutes. The web has no device id: it checks the email instead.
        </p>
      </GridBand>
      <GridCell className="space-y-4">
        {s ? (
          <ul className="space-y-1 text-sm">
            <li>Check: {s.deviceCheckEnabled ? "on" : "off (switch on the Free card)"}</li>
            <li>Android / Mac salt secret: {s.saltSet ? "set" : "not set, devices are not checked yet"}</li>
            <li>iPhone DeviceCheck key: {s.appleDeviceCheckSet ? "set" : "not set, iPhones are not checked yet"}</li>
            <li>
              Devices on the list: {count(s.devices)} · Accounts decided: {count(s.decisions)} (
              {s.decisions.map((d) => `${outcomeLabel(d.outcome).toLowerCase()} ${d.n}`).join(", ") || "none"})
            </li>
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">{grants.isError ? "Couldn't load." : "Loading…"}</p>
        )}

        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(search.trim());
          }}
        >
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Find an account by email"
            className="max-w-sm"
            aria-label="Find an account by email"
          />
          <Button type="submit" size="sm" variant="outline" disabled={search.trim().length < 3}>
            Find
          </Button>
        </form>

        {(grants.data?.accounts ?? []).map((a) => (
          <div key={a.id} className="rounded-xl border border-app-border p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-medium">{a.email ?? a.id}</p>
                <p className="text-muted-foreground">
                  {outcomeLabel(a.outcome)}
                  {a.platform ? ` · ${a.platform}` : ""}
                  {a.override_at ? " · allowed by Super Admin" : ""}
                </p>
              </div>
              {a.outcome !== "granted" ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={act.isPending}
                  onClick={() => act.mutate(() => apiPost(`${adminEndpoints.freeGrants}/users/${a.id}/allow`, {}))}
                >
                  Allow free minutes
                </Button>
              ) : null}
            </div>
            {a.devices.map((d) => (
              <div key={String(d.id)} className="mt-2 flex flex-wrap items-center justify-between gap-2 text-muted-foreground">
                <span>
                  {d.platform}
                  {d.hash_start ? ` · ${d.hash_start}…` : " · Apple DeviceCheck"}
                  {d.cleared_at ? " · cleared" : ""}
                </span>
                {!d.cleared_at && d.platform !== "ios" ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={act.isPending}
                    onClick={() =>
                      act.mutate(() => apiPost(`${adminEndpoints.freeGrants}/devices/${d.id}/clear`, { note: "Support" }))
                    }
                  >
                    Clear device
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        ))}
        {submitted && grants.data && grants.data.accounts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No account matches.</p>
        ) : null}
      </GridCell>

      <GridCell className="space-y-3">
        <div>
          <p className="text-sm font-medium">Disposable email domains</p>
          <p className="text-sm text-muted-foreground">
            Accounts on these domains get no free minutes (the account still works). The list refreshes every day;
            {disposable.data
              ? ` ${disposable.data.listSize.toLocaleString("en-GB")} domains${disposable.data.refreshedAt ? `, last ${new Date(disposable.data.refreshedAt).toLocaleDateString("en-GB")}` : ""}.`
              : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={domain}
            onChange={(e) => setDomain(e.target.value.trim().toLowerCase())}
            placeholder="example.com"
            className="max-w-xs"
            aria-label="Email domain"
          />
          <Button
            size="sm"
            variant="outline"
            disabled={!/^[a-z0-9.-]+\.[a-z0-9-]+$/.test(domain) || act.isPending}
            onClick={() => act.mutate(() => apiPut(`${adminEndpoints.freeGrants}/disposable/${encodeURIComponent(domain)}`, { blocked: true }))}
          >
            Block
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={!/^[a-z0-9.-]+\.[a-z0-9-]+$/.test(domain) || act.isPending}
            onClick={() => act.mutate(() => apiPut(`${adminEndpoints.freeGrants}/disposable/${encodeURIComponent(domain)}`, { blocked: false }))}
          >
            Allow
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={act.isPending}
            onClick={() => act.mutate(() => apiPost(`${adminEndpoints.freeGrants}/disposable/refresh`, {}))}
          >
            Refresh list now
          </Button>
        </div>
        {(disposable.data?.adminRows ?? []).length ? (
          <ul className="space-y-1 text-sm">
            {disposable.data!.adminRows.map((r) => (
              <li key={r.domain} className="flex items-center justify-between gap-2">
                <span>
                  {r.domain} · {r.blocked ? "blocked" : "allowed"} by Super Admin
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={act.isPending}
                  onClick={() => act.mutate(() => apiDelete(`${adminEndpoints.freeGrants}/disposable/${encodeURIComponent(r.domain)}`))}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
      </GridCell>
    </>
  );
}
